import clsx from "clsx";
import {
  ArrowDownToDot,
  EllipsisVertical,
  LucideIcon,
  Merge,
  Pen,
  Sparkles,
  SquareSplitVertical,
  Trash2,
} from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { DoclistTreeItem, DoclistTreeItemSectionsItem } from "~/api/model";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { trackEvent } from "~/utils/analytics";
import { CategoryItem } from "../../types";

interface SectionActionsProps {
  section: DoclistTreeItemSectionsItem;
  className?: string;
  handleOpenChange: (isOpen: boolean) => void;
  handleRename: () => void;
  onAutoRename: () => void;
  isAiRenaming?: boolean;
  categories: CategoryItem[];
  onMoveTo: () => void;
  onSplit: () => void;
  items: DoclistTreeItem[];
  onMerge: () => void;
  onDelete: () => void;
  isMultiSelectMode?: boolean;
  isSelectedForMerge?: boolean;
}

interface ActionItem {
  id: string;
  icon: LucideIcon;
  messageId: string;
  defaultMessage: string;
  className?: string;
  action: () => void;
  disabled?: boolean;
}

export const SectionActions: FC<SectionActionsProps> = ({
  section,
  className = "",
  handleOpenChange,
  handleRename,
  onAutoRename,
  isAiRenaming = false,
  categories,
  onMoveTo,
  onSplit,
  items,
  onMerge,
  onDelete,
  isMultiSelectMode = false,
  isSelectedForMerge = false,
}) => {
  const [open, setOpen] = useState(false);

  const hasMultipleSections = items.flatMap((item) => item.sections).length > 1;

  const actionItems = [
    !isMultiSelectMode && {
      id: "editName",
      icon: Pen,
      messageId: "docList.section.actionsDropdown.rename",
      defaultMessage: "Rename",
      action: handleRename,
    },
    !isMultiSelectMode && {
      id: "aiRename",
      icon: Sparkles,
      messageId: "docList.section.actionsDropdown.aiRename",
      defaultMessage: "AI Rename",
      action: onAutoRename,
      disabled: isAiRenaming,
    },
    categories.length > 0 && {
      id: "moveTo",
      icon: ArrowDownToDot,
      messageId: "docList.section.actionsDropdown.moveTo",
      defaultMessage: "Move To...",
      action: () => onMoveTo(),
    },
    !isMultiSelectMode &&
      section.pageCount > 1 && {
        id: "split",
        icon: SquareSplitVertical,
        messageId: "docList.section.actionsDropdown.split",
        defaultMessage: "Split",
        action: () => {
          onSplit();
        },
      },
    hasMultipleSections && {
      id: "merge",
      icon: Merge,
      messageId: "docList.section.actionsDropdown.merge",
      defaultMessage: "Merge",
      action: () => {
        onMerge();
      },
    },
    hasMultipleSections && {
      id: "delete",
      icon: Trash2,
      messageId: "docList.category.actionsDropdown.delete",
      defaultMessage: "Delete",
      action: () => {
        onDelete();
      },
    },
  ].filter(Boolean) as ActionItem[];

  useEffect(() => {
    handleOpenChange(open);
  }, [open]);

  useEffect(() => {
    if (open) {
      trackEvent({ eventName: "app/doc_list/document_actions.button/click" });
    }
  }, [open]);

  if (isSelectedForMerge) {
    return null;
  }

  return (
    <>
      <div onClick={(e) => e.stopPropagation()}>
        <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
          <DropdownMenuTrigger asChild>
            <EllipsisVertical className={clsx("w-4 h-4", className)} />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-32" align="end">
            {actionItems.map((item) => {
              const actionItem = item as ActionItem;
              const IconComponent = actionItem.icon;
              return (
                <DropdownMenuItem
                  key={actionItem.id}
                  className={clsx(
                    "cursor-pointer",
                    actionItem.className,
                    actionItem.disabled && "opacity-50 cursor-not-allowed"
                  )}
                  onClick={actionItem.disabled ? undefined : actionItem.action}
                  destructive={actionItem.id === "delete"}
                  disabled={actionItem.disabled}
                >
                  <IconComponent className="mr-2 h-4 w-4" />
                  <FormattedMessage
                    id={actionItem.messageId}
                    defaultMessage={actionItem.defaultMessage}
                  />
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
};
